import { AppAssistApi } from "./AppAssistApi";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppExportRules } from "./AppExportRules";
import { AppBrowserSettings } from "./AppBrowserSettings";
import { AppAssistDone } from "./AppAssistPanel";

// AppAiOutcome - the page half of the persisted AI history that is not a read: recording what the owner did
// with a draft (`sent`, `edited`, `skipped`) on its item in the contact's `ai-chat.json`, through `/assist`'s
// outcome action, and remembering per browser whether the AI panel is open.
//
// ⛔ AN OUTCOME RECORDS - IT NEVER SENDS. The send already went through `/send`; this only writes down that
// it did, so a reload never offers that draft again.
// ⚠ ONE RETRY on a lost connection, three seconds later; a second failure is ONE ink sentence in the panel
// (`IAiOutcomeHost.NotNoted`) - handled or reported, never hidden. A service without the route (no boolean
// `ok`) has no history to note it in, and says nothing.

/** What an outcome send needs from the page. */
export interface IAiOutcomeHost {
    /** POST the body to `/assist`; `Done` answered exactly once - (0, "") for a lost connection. */
    Post(Body: string, Done: AppAssistDone): void;
    /** The outcome could not be noted - say so where the owner will see it. */
    NotNoted(): void;
}

/** One outcome on its way to the service - retried once on a lost connection. */
export class AppAiOutcomeSend {
    /** A field so the suite can shorten it. */
    public static RetryMs: number = 3000;
    public static readonly MaxTries: number = 2;

    private Host: IAiOutcomeHost;
    private Body: string;
    private Tries: number = 0;
    /** ⭐ Quiet re-asks after `retry` refusals (the archive still being restored after a restart, 2026-09-29). */
    public static readonly MaxAgain: number = AppExportRules.MaxAgain;
    private Waits: number = 0;

    public constructor(Host: IAiOutcomeHost, Body: string) {
        this.Host = Host;
        this.Body = Body;
    }

    public Go = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Body.length === 0) { return; }
            this.Tries = this.Tries + 1;
            this.Host.Post(this.Body, this.OnDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiOutcomeSend.Go");
        }
    };

    /** Noted, not routed (nothing to note it in), retried once, or said. */
    public OnDone = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Verdict: string = AppAiOutcomeSend.VerdictOf(Status, Text);
            if (Verdict === "ok" || Verdict === "none") { return; }
            const Wait: number = AppExportRules.AgainAfterMs(
                { Ok: false, RetrySec: AppAiOutcomeSend.LaterMsOf(Status, Text) / 1000 }, this.Waits);
            if (Wait > 0) {
                this.Waits = this.Waits + 1;
                setTimeout(this.Again, Wait);
                return;
            }
            if (Verdict === "lost" && this.Tries < AppAiOutcomeSend.MaxTries) {
                setTimeout(this.Go, AppAiOutcomeSend.RetryMs);
                return;
            }
            this.Host.NotNoted();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiOutcomeSend.OnDone");
        }
    };

    /** The same outcome, posted again after a `retry` refusal (not counted against the lost-connection retry). */
    private Again = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Host.Post(this.Body, this.OnDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiOutcomeSend.Again");
        }
    };

    /**
     * ms to wait before asking again when the service said `retry` (1-600 s), else 0.
     * DOCUMENTED EXCEPTION: pure parse of an answer in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: an unreadable body is "no retry" - `VerdictOf` already decided what it is.
     */
    public static LaterMsOf = (Status: number, Text: string): number => {
        try {
            if (Status === 0) { return 0; }
            const Doc: Record<string, unknown> = JSON.parse(nz(Text)) as Record<string, unknown>;
            if (Doc === null || typeof Doc !== "object" || Doc.ok !== false) { return 0; }
            return AppAssistApi.RetryOf(Doc.retry) * 1000;
        } catch (e) {
            return 0;
        }
    };

    /**
     * `ok`, `none` (a service without the route), `lost` (no answer at all) or `refused`.
     * DOCUMENTED EXCEPTION: pure parse of an answer in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: an unreadable body is "lost" - the caller retries once, then says so.
     */
    public static VerdictOf = (Status: number, Text: string): string => {
        try {
            if (Status === 0) { return "lost"; }
            const Raw: unknown = JSON.parse(nz(Text));
            if (Raw === null || typeof Raw !== "object") { return "lost"; }
            const Doc: Record<string, unknown> = Raw as Record<string, unknown>;
            if (typeof Doc.ok !== "boolean") { return "none"; }
            if (Doc.ok === true) { return "ok"; }
            return "refused";
        } catch (e) {
            return "lost";
        }
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static NotNotedWhy = (): string => {
        return "What you did with that suggestion could not be noted in this chat's AI history - it may be"
            + " offered again after a reload.";
    };
}

/**
 * Whether the AI panel was closed by this browser's owner - the one thing kept in `localStorage` for it.
 * ⛔ EVERY CALL IS WRAPPED: the accessor THROWS in private browsing, under a blocked-storage policy and at
 * quota (`AppArchiveStore`'s measured rule). A refusal is simply "not remembered": the default holds.
 */
export class AppAiPanelStore {
    public static readonly Key: string = "wac.aiPanel";

    /**
     * "closed", "open" or "" (nothing remembered).
     * ERROR-SUPPRESSED-JUSTIFIED: a storage refusal is "nothing remembered" - the panel's default applies.
     */
    public static Read = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return nz(AppBrowserSettings.Local().ReadText(AppAiPanelStore.Key).Value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiPanelStore.Read");
            return "";
        }
    };

    /** A refusal is answered by the shared class as a plain outcome (never reported, never kept in memory): the choice is just not remembered next time. */
    public static Write = (Value: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppBrowserSettings.Local().WriteText(AppAiPanelStore.Key, nz(Value));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiPanelStore.Write");
        }
    };

}
